import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { collectResults } from 'utils/query'
import { ManageAccountsPanelProps } from 'screens/BrandKit/LinkedAccountsDrawer/Types'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './ManagaAccountsPanel.module.scss'
import { Button } from 'blaze-ui/Button'
import { AccountSkeleton } from 'screens/BrandKit/LinkedAccountsDrawer/AccountSkeleton'
import { Account } from './Account'
import { AccountsList } from 'screens/BrandKit/LinkedAccountsDrawer/AccountsList'
import { AccountListItem } from 'screens/BrandKit/LinkedAccountsDrawer/AccountListItem'
import { useIsMutating } from 'hooks/useIsMutating'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Trash2 } from 'blaze-ui/icons'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useConfirmDelete } from 'hooks/useConfirmDelete'
import { useCallback } from 'react'
import { BrandKitSource } from 'types'
import { startCase } from 'lodash'

export const ManageAccountsPanel = ({ brandKitId, onLinkMoreAccounts }: ManageAccountsPanelProps) => {
  const linkedAccountsQuery = useBrandKitSources(
    { brandKitId, sourceType: ['ExternalIntegration'] },
    { cacheTime: 1000 * 60 * 5 },
  )
  const confirmDelete = useConfirmDelete()
  const linkedAccounts = collectResults(linkedAccountsQuery, (data) => data.brand_kit_sources)
  const isMutating = useIsMutating({ mutationKey: 'createBrandKitSource' })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource({
    onSuccess: async () => {
      const result = await linkedAccountsQuery.refetch()
      if (!result.data?.pages[0].data.brand_kit_sources.length) {
        onLinkMoreAccounts()
      }
    },
  })

  const handleDelete = useCallback(
    async (linkedAccount: BrandKitSource<ExternalIntegration>) => {
      const confirm = await confirmDelete({
        msg: `You are about to remove your ${startCase(
          linkedAccount.source.provider,
        )} linked account from this Brand Kit`,
        confirmText: 'Remove Linked Account',
      })
      if (confirm) {
        deleteBrandKitSource({ brandKitId, id: linkedAccount.id })
      }
    },
    [confirmDelete, deleteBrandKitSource, brandKitId],
  )

  return (
    <div className={Styles.root}>
      <AccountsList>
        {(linkedAccountsQuery.isLoading || isMutating > 0) &&
          Array.from({ length: isMutating || 2 }).map((_, i) => (
            <AccountListItem key={i}>
              <AccountSkeleton />
            </AccountListItem>
          ))}
        {linkedAccountsQuery.isSuccess &&
          linkedAccounts.map((linkedAccount) => (
            <AccountListItem key={linkedAccount.id}>
              <Account account={linkedAccount.source}>
                <IconButton
                  onPress={() => handleDelete(linkedAccount)}
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${linkedAccount.source.name} Linked Account`}
                  icon={Trash2}
                  color="var(--dark-80)"
                />
              </Account>
            </AccountListItem>
          ))}
      </AccountsList>
      <Button fullWidth size="lg" onPress={() => onLinkMoreAccounts()} variant="tertiary">
        Add More Accounts
      </Button>
    </div>
  )
}
